import React, { lazy, Suspense, useEffect, useState, useMemo, useContext } from 'react';
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'components/layout';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/InlineLoader';
import ScrollToTop from 'components/scroll-to-top';
import { toast } from 'react-toastify';
import Progress from 'react-progress';
import 'react-toastify/dist/ReactToastify.min.css';
import 'react-multi-carousel/lib/styles.css';
import { Helmet } from 'react-helmet';
import { GlobalStyles } from 'assets/styled/global.styled';
import { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from 'assets/styled/Theme';
import useGaTracker from 'useGatracker';
import CookieConsent from 'react-cookie-consent';
import jwt from 'jwt-decode';
import { setUserSub } from 'redux/userSubInfo/userSub.slice';
import firebase from 'firebase';
import useAnanymousCheck from 'useAnanymousCheck';
import SubscribeRedirect from 'components/settings/SubscribeRedirect';
import { setVastTag } from 'redux/video/video.slice';
import { callbackTypeOne } from 'utils/gp-callbacks';
import VideoDetailSlug from 'pages/VideoDetailSlug';
import SeriesDetailSlug from 'pages/SeriesDetailSlug';
import MetaPixel from 'utils/metaPixel';
import { lazyWithRetry } from 'utils/module-loader';
import TvodExclusiveSlug from 'pages/TvodExclusiveSlug';
import GpRedirection from 'pages/GpRedirection';
import { postPayment, socialSubscribeHandler } from 'utils/payment.utils';
import PlayGpExclusive from 'pages/PlayGpExclusive';
import { safeJSONParse, safeJwtDecode } from 'utils/safeJwts';
import { checkTvodSubscription } from 'redux/video/video.utils';
import { signOutStart } from 'redux/user/user.actions';
import { initGpt } from 'utils/gptAdManager';

// Dynamic Imports Goes Here.
// const SearchPage = lazy(() => import('pages/SearchPage'));

// const UserSettings = React.lazyWithRetry(() => import(/* webpackChunkName: "userSettings" */ './settings')));
const SearchPage = lazyWithRetry(() => import('pages/SearchPage'));
const FavouriteSeeAll = lazyWithRetry(() => import('pages/FavouriteSeeAll'));
const ScrollToTopButton = lazyWithRetry(() => import('components/ScrollToTopButton'));
const PremiumModal2 = lazyWithRetry(() => import('components/settings/PremiumModal2'));
const Package1 = lazyWithRetry(() => import('components/settings/Package1'));
const ChannelsSeemore = lazyWithRetry(() => import('pages/ChannelsSeemore'));
const AllPopularArtist = lazyWithRetry(() => import('pages/AllPopularArtist'));
const Settings = lazyWithRetry(() => import('pages/Settings'));
const ArtistPageRebuilt = lazyWithRetry(() => import('pages/ArtistPageRebuilt'));
const CouponAuth = lazyWithRetry(() => import('components/CouponAuth'));
const Modals = lazyWithRetry(() => import('components/modals'));
const Login = lazyWithRetry(() => import('pages/Login'));
const PaymentConfirmation = lazyWithRetry(() => import('pages/PaymentConfirmation'));
const CallbackPaymentPage = lazyWithRetry(() => import('pages/CallbackPaymentPage'));
const Home = lazyWithRetry(() => import('pages/Home'));
const Audios = lazyWithRetry(() => import('pages/Audios'));
const Videos = lazyWithRetry(() => import('pages/Videos'));
const Movie = lazyWithRetry(() => import('pages/Movie'));
const AudioDetail = lazyWithRetry(() => import('pages/AudioDetail'));
const AudioDetailSlug = lazyWithRetry(() => import('pages/AudioDetailSlug'));
const AlbumDetail = lazyWithRetry(() => import('pages/AlbumDetail'));
const AlbumDetailSlug = lazyWithRetry(() => import('pages/AlbumDetailSlug'));
const VideoDetail = lazyWithRetry(() => import('pages/VideoDetail'));
const ChannelPlay = lazyWithRetry(() => import('pages/ChannelPlay'));
const SeriesDetail = lazyWithRetry(() => import('pages/SeriesDetail'));
const CatchupDetail = lazyWithRetry(() => import('pages/CatchupDetail'));
const CategoryPage = lazyWithRetry(() => import('pages/CategoryPage'));
const CategoryPageSlug = lazyWithRetry(() => import('pages/CategoryPageSlug'));
const RecommendationPage = lazyWithRetry(() => import('pages/RecommendationPage'));
const Favourite = lazyWithRetry(() => import('pages/Favourite'));
const NotFound = lazyWithRetry(() => import('pages/NotFound'));
const PrivacyPolicy = lazyWithRetry(() => import('pages/PrivacyPolicy'));
const TermsOfUse = lazyWithRetry(() => import('pages/TermsOfUse'));
const AboutCompany = lazyWithRetry(() => import('pages/AboutCompany'));
const PlaystorePopup = lazyWithRetry(() => import('components/PlaystorePopup'));
const ContactUs = lazyWithRetry(() => import('pages/ContactUs'));
const Pricing = lazyWithRetry(() => import('pages/Pricing'));
const Original = lazyWithRetry(() => import('pages/Originals'));
const UpComing = lazyWithRetry(() => import('pages/UpComing'));
const BBCUrdu = lazyWithRetry(()=> import('pages/BBCUrdu'));
const Shorts = lazyWithRetry(() => import('pages/ShortsPageVideojs'));
const ShortsBySlug = lazyWithRetry(() => import('pages/ShortsPageBySlug'));
const VideoDetailSlugRedesign = lazyWithRetry(() => import('pages/VideoDetailSlugRedesign'));
const SeriesDetailSlugRedesign = lazyWithRetry(() => import('pages/SeriesDetailSlugRedesign'));
const PlayContent = lazyWithRetry(() => import('pages/PlayContent'));
const PlaySeries = lazyWithRetry(() => import('pages/PlaySeries'));
const PlayTrailer = lazyWithRetry(() => import('pages/PlayTrailer'));
const GpPaymentFail = lazyWithRetry(() => import('pages/GpPaymentFail'));
const FbPromotion = lazyWithRetry(() => import('pages/FbPromotion'));
const ConnectTv = lazyWithRetry(() => import('pages/ConnectTv'));

const IndicatorContext = React.createContext();

function IndicatorProvider({ children }) {
  const [active, setActive] = useState(false);
  const value = useMemo(() => ({ active, setActive }), [active, setActive]);
  return <IndicatorContext.Provider value={value}>{children}</IndicatorContext.Provider>;
}

function Indicator() {
  const { active } = useContext(IndicatorContext);
  const [percent, setPercent] = useState(0);
  useEffect(() => {
    setTimeout(() => {
      setPercent((percent) => (percent < 100 ? percent + 10 : 100));
    }, 200);
  });
  return active ? <Progress percent={percent} /> : null;
}

function IndicatorFallback() {
  const { setActive } = useContext(IndicatorContext);
  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  });
  return null;
}

function PrivateRoute({ children, ...rest }) {
  const { currentUser, status } = useSelector((state) => state.user);
  if (!currentUser && status === 'pending') return <Loader />;

  return (
    <Route
      {...rest}
      render={({ location }) =>
        currentUser ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location }
            }}
          />
        )
      }
    />
  );
}
toast.configure();

// const addStyle = (url) => {
//   const style = document.createElement('link');
//   style.href = url;
//   style.rel = 'stylesheet';
//   style.async = true;

//   document.head.appendChild(style);
// };

export default function App() {
  const { theme } = useSelector((state) => state.theme);
  const { currentUser, status } = useSelector((state) => state.user);
  const location = useLocation();
  const from = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useDispatch();
  const history = useHistory();
  const analytics = firebase.analytics();
  const title = window.platform_info.title;
  const iosUser = safeJSONParse(localStorage.getItem(`ios_sub_user_${title}`));
  const robiUser = safeJSONParse(localStorage.getItem(`robi_user_${title}`));
  const gpUser = safeJSONParse(localStorage.getItem(`gp_user_${title}`));
  const blUser = safeJSONParse(localStorage.getItem(`bl_user_${title}`));
  const asthaUser = safeJSONParse(localStorage.getItem(`astha_user_${title}`));
  useAnanymousCheck();

  React.useEffect(() => {
    // Setting Vast Tag
    const vastAdRef = firebase.database().ref('vast_ads');
    vastAdRef.child('web').once('value', (snap) => {
      if (snap.exists()) {
        dispatch(setVastTag(snap.val()));
      }
    });
  }, []);

  useEffect(() => {
    const hash = searchParams.get('hash');
    if (hash) {
      try {
        const user = jwt(hash);
        if (user['subscribe'] !== undefined) {
          localStorage.setItem(`astha_user_${title}`, JSON.stringify(user));
          localStorage.setItem(`access_token_r_${title}`, JSON.stringify(hash));
          localStorage.setItem(`refresh_token_r_${title}`, JSON.stringify(hash));
          localStorage.removeItem(`access_token_temp_${title}`);
          localStorage.removeItem(`refresh_token_temp_${title}`);
          if (history?.push && from) history.push(from);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  }, [location.search]);

  // Gp Users access to homepage
  useEffect(() => {
    const token = searchParams.get('token');
    console.log('imprinted here');
    let decodedGPTOken;
    if (token) {
      try {
        decodedGPTOken = jwt(token);
      } catch (error) {
        document.getElementById('root').innerText = `Invalid Token Error`;
      }
    }
    // if url coming with series or video route
    // const fromSeriesOrVideo = location.pathname.startsWith('/video') || location.pathname.startsWith('/series');

    if (decodedGPTOken?.partner && decodedGPTOken?.partner == 'GP_MYGP') {
      try {
        console.log('Triggering from Appjs component');
        callbackTypeOne(decodedGPTOken?.exp);

        localStorage.setItem(`gp_user_${title}`, JSON.stringify(decodedGPTOken));
        localStorage.setItem(`access_token_r_${title}`, JSON.stringify(token));
        localStorage.setItem(`refresh_token_r_${title}`, JSON.stringify(token));
        console.log('imprinted here 2');
        localStorage.removeItem(`access_token_temp_${title}`);
        localStorage.removeItem(`refresh_token_temp_${title}`);
        // if (!fromSeriesOrVideo) {
        //   history.push(`/`);
        // }
      } catch (error) {
        console.log('error', error);
      }
    }
    if (decodedGPTOken?.partner && decodedGPTOken?.partner == 'ROBI_MYROBI') {
      try {
        localStorage.setItem(`robi_user_${title}`, JSON.stringify(decodedGPTOken));
        localStorage.setItem(`access_token_r_${title}`, JSON.stringify(token));
        localStorage.setItem(`refresh_token_r_${title}`, JSON.stringify(token));
        localStorage.removeItem(`access_token_temp_${title}`);
        localStorage.removeItem(`refresh_token_temp_${title}`);
        // if (!fromSeriesOrVideo) {
        //   history.push(`/`);
        // }
      } catch (error) {
        console.log('error', error);
      }
    }
    if (decodedGPTOken?.partner && decodedGPTOken?.partner == 'BANGLALINK_MYBL') {
      try {
        console.log('Triggering from Appjs component');
        callbackTypeOne(decodedGPTOken?.exp);

        localStorage.setItem(`bl_user_${title}`, JSON.stringify(decodedGPTOken));
        localStorage.setItem(`access_token_r_${title}`, JSON.stringify(token));
        localStorage.setItem(`refresh_token_r_${title}`, JSON.stringify(token));
        localStorage.removeItem(`access_token_temp_${title}`);
        localStorage.removeItem(`refresh_token_temp_${title}`);
        // if (!fromSeriesOrVideo) {
        //   history.push(`/`);
        // }
      } catch (error) {
        console.log('error', error);
      }
    }
  }, [location.search]);

  let gTag = window.platform_info.google_analytics_key;
  const adsToken = window?.google_adsense?.adsense_code;
  const apikey = window?.platform_info?.api_key;
  //const tawktoSource = window?.tawkto?.src;

  useGaTracker();
  // addStyle('http://localhost:9000/rockerzs.css');

  /**
 * Imports google adsense to the project
 
  const injectAds = () => {
    const script = document.createElement('script');
    script.src = adsToken;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';

    document.body.appendChild(script);
  };
  useEffect(() => {
    if (adsToken && apikey === 'iscreen') {
      injectAds();
    }
  }, [adsToken, apikey]);
*/

  /**  Revive Adserver - Integration
  const REVIVE_SRC = 'https://servedby.revive-adserver.net/asyncjs.php';
  useEffect(() => {
    if (window.__reviveScriptLoaded) return;

    const script = document.createElement('script');
    script.src = REVIVE_SRC;
    script.async = true;
    script.id = 'revive-adserver-script';

    script.onload = () => {
      window.__reviveScriptLoaded = true;
    };

    document.body.appendChild(script);
  }, []);
  */

  // Tawkto Integration
  // useEffect(() => {
  //   if (tawktoSource) {
  //     var Tawk_API = Tawk_API || {}, Tawk_LoadStart = new Date();
  //     (function () {
  //       var s1 = document.createElement("script"), s0 = document.getElementsByTagName("script")[0];
  //       s1.async = true;
  //       s1.src = tawktoSource;
  //       s1.charset = 'UTF-8';
  //       s1.setAttribute('crossorigin', '*');
  //       s0.parentNode.insertBefore(s1, s0);
  //     })();
  //   }
  // }, [tawktoSource])

  // Google Ad Manager (gpt.js) - Integration.
  // Init is owned by utils/gptAdManager (disableInitialLoad + SRA + enableServices);
  // individual slots register/refresh themselves through the same module.
  useEffect(() => {
    initGpt();
  }, []);

  useEffect(() => {
    if (currentUser) {
      console.log('Checking whether new token dispatched for subscription');
      const accessToken = safeJSONParse(localStorage.getItem(`access_token_r_${title}`));
      console.log('imprinted here 5 with access token');
      try {
        const user = safeJwtDecode(accessToken);
        if (user) dispatch(setUserSub(user));
      } catch (error) {
        document.getElementById('root').innerText = `Invalid Token Error`;
      }
      console.log('imprinted here 6 with access token');
    }
  }, [currentUser]);

  // Payment Page Landing
  useEffect(() => {
    async function paymentLandingWithLogin() {
      const reference = searchParams.get('ref');
      const tvod = searchParams.get('tvod');
      const contentId = searchParams.get('cid');
      const contentSlug = searchParams.get('slug');
      const packageId = searchParams.get('package');
      const productId = searchParams.get('product');
      if (reference === 'paymentlanding') {
        if (status === 'resolved') {
          console.log('Status Resolved');
          if (currentUser) {
            console.log('paymentLanding found currentuser');
            // window.location.href = 'https://youtube.com';
            if (tvod) {
              checkTvodSubscription(contentId)
                .then((res) => {
                  const tvodTrue = res?.data.data.status;
                  if (tvodTrue) {
                    history.push(`/content/play/video/${contentSlug}`);
                  } else {
                    postPayment(packageId, productId)
                      .then((res) => {
                        console.log('successfully redirecting payment provider', res);
                      })
                      .catch((err) => {
                        if (err === 400) {
                          dispatch(signOutStart());
                          localStorage.removeItem(`access_token_r_${title}`);
                          localStorage.removeItem(`refresh_token_r_${title}`);
                          localStorage.removeItem(`coupon_login_${title}`);
                          localStorage.removeItem(`otp_login_bd_${title}`);
                          history.push({
                            pathname: '/gp-payment-fail',
                            search: `?cid=${contentId}&tvod=true&slug=${contentSlug}&package=${packageId}&product=${productId}`
                          });
                        }
                      });
                  }
                })
                .catch((err) => {
                  console.error('Error checking TVOD subscription:', err);
                });
            } else {
              try {
                const accessToken = safeJSONParse(localStorage.getItem(`access_token_r_${title}`));
                const user = safeJwtDecode(accessToken);
                if (user?.subscribe) {
                  history.push('/');
                } else {
                  // socialSubscribeHandler();
                  postPayment(packageId, productId)
                    .then((res) => {
                      console.log('successfully redirecting payment provider', res);
                    })
                    .catch((err) => {
                      if (err === 400) {
                        dispatch(signOutStart());
                        localStorage.removeItem(`access_token_r_${title}`);
                        localStorage.removeItem(`refresh_token_r_${title}`);
                        localStorage.removeItem(`coupon_login_${title}`);
                        localStorage.removeItem(`otp_login_bd_${title}`);
                        history.push({
                          pathname: '/gp-payment-fail',
                          search: `?package=${packageId}&product=${productId}`
                        });
                      }
                    });
                }
              } catch (error) {
                console.error('Error Redirecting homepage for subscribed user', error);
              }
            }
          } else {
            console.log('paymentLanding found login');
            if (tvod) {
              history.push({
                pathname: '/login',
                // state: { from: { pathname: '?ref=paymentlanding' } }
                search: `?ref=paymentlanding&tvod=true&cid=${contentId}&slug=${contentSlug}&package=${packageId}&product=${productId}`
              });
            } else {
              history.push({
                pathname: '/login',
                search: `?ref=paymentlanding&package=${packageId}&product=${productId}`
              });
            }
          }
        }
      }
    }

    paymentLandingWithLogin();
  }, [searchParams.get('ref'), currentUser, status]);

  //Astha User
  useEffect(() => {
    if (window.location.href == 'https://iscreen.com.bd/' && asthaUser) {
      console.log('Astha Hit count');
      analytics.logEvent('third_party_inapp_hit', {
        platform: window?.platform_info.api_key,
        partner: asthaUser?.username,
        hit: 1
      });
    }
  }, [asthaUser]);

  return (
    <IndicatorProvider>
      <Indicator />
      <ScrollToTop />
      <MetaPixel />

      {/*
        Deliberately carries no title, description or og tags. This Helmet mounts on
        every route, so anything declared here overrode the per-URL metadata the
        server injects (server/seo.js) with the bare platform name — generic title
        and description sitewide, and a duplicate og:title/og:image alongside the
        server's. Page-level metadata belongs to the server; per-page Helmets add
        only what changes during client-side navigation.
      */}
      <Helmet>
        <link rel="icon" type="image/png" href={window.platform_info.favIcon} sizes="16x16" />
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${gTag}`}></script>
      </Helmet>

      {/* {(asthaUser && asthaUser.username == 'astha') || gpUser || robiUser ? (
          ''
        ) : iosUser ? (
          ''
        ) : (
          <PlaystorePopup />
        )} */}
      <Layout>
        <Suspense fallback={<IndicatorFallback />}>
          <ThemeProvider theme={theme === 'dark' ? darkTheme : lightTheme}>
            <GlobalStyles />
            <ErrorBoundary>
              <ScrollToTopButton />
              {iosUser || gpUser || robiUser || blUser ? (
                ''
              ) : (
                <CookieConsent
                  location="bottom"
                  buttonText="Accept"
                  cookieName="accept-cookie"
                  style={{
                    alignItems: 'center',
                    background: '#fff',
                    color: '#000',
                    left: window.innerWidth > 1500 ? '5%' : '0%',
                    right: '5%',
                    maxWidth: '1420px',
                    margin: '20px auto',
                    padding: '1.5em 1.86em',
                    fontSize: '13px',
                    borderRadius: '4px'
                  }}
                  buttonStyle={{
                    color: '#fff',
                    fontSize: '13px',
                    backgroundColor: '#000',
                    fontWeight: 'bold',
                    width: '12.86em',
                    maxWidth: '16em',
                    padding: '.625em 2em',
                    borderRadius: '4px'
                  }}
                  expires={150}>
                  To give you the best experience, we use necessary cookies for core functions. With
                  your permission, we can also use additional cookies to tailor content, analyze
                  visits, and improve our site. Click "Accept" to enjoy a fully optimized
                  experience—learn more in our{' '}
                  <a
                    target="_blank"
                    href={`https://urduflix.com/privacy-policy`}
                    style={{ color: '#000' }}>
                    Policy
                  </a>
                </CookieConsent>
              )}
              <Switch>
                <Route exact path="/login">
                  <Login />
                </Route>
                <Route exact path="/coupon-login">
                  <CouponAuth />
                </Route>
                <Route exact path="/payment/confirm/:status">
                  <PaymentConfirmation />
                </Route>
                <Route exact path="/gp-payment-fail">
                  <GpPaymentFail />
                </Route>
                <Route exact path="/callback">
                  <CallbackPaymentPage />
                </Route>
                <Route exact path="/videos">
                  <Videos />
                </Route>
                <Route exact path="/movie">
                  <Movie />
                </Route>
                <Route exact path="/series">
                  <Movie />
                </Route>
                <Route exact path="/songs">
                  <Audios />
                </Route>
                <Route exact path="/feed">
                  <Original />
                </Route>
                <Route exact path="/upcoming">
                  <UpComing />
                </Route>
                <Route exact path="/bbc-urdu">
                  <BBCUrdu />
                </Route>
                {/*
                <Route path="/artist/:id">
                  <ArtistPage />
                </Route> */}
                <Route path="/artist/:slug">
                  <ArtistPageRebuilt />
                </Route>
                <Route exact path="/video/:id">
                  <VideoDetail />
                </Route>
                <Route exact path="/content/video/:slug">
                  <VideoDetailSlugRedesign />
                </Route>
                <Route exact path="/content/details/video/:slug">
                  <VideoDetailSlugRedesign />
                </Route>
                {/* Actual Player routes Starts */}
                <Route exact path="/content/play/video/:slug">
                  <PlayContent />
                </Route>
                <Route exact path="/content/play/series/:slug">
                  <PlaySeries />
                </Route>
                <Route exact path="/play/clip/:slug">
                  <PlayTrailer />
                </Route>
                <Route exact path="/play/gpexclusive/:slug">
                  <PlayGpExclusive />
                </Route>
                {/* Actual Player routes Ends */}
                <Route exact path="/content/tvodexclusive/:slug">
                  <TvodExclusiveSlug />
                </Route>
                <Route exact path="/purchase/mygp/redirect">
                  <GpRedirection />
                </Route>
                <Route exact path="/content/tv/:slug">
                  <VideoDetailSlug />
                </Route>
                <Route exact path="/channel/:id">
                  <ChannelPlay />
                </Route>
                <Route exact path="/channels">
                  <ChannelsSeemore />
                </Route>
                <Route exact path="/series/:id">
                  <SeriesDetail />
                </Route>
                <Route exact path="/content/series/:slug">
                  <SeriesDetailSlugRedesign />
                </Route>
                <Route exact path="/content/details/series/:slug">
                  <SeriesDetailSlugRedesign />
                </Route>
                <Route exact path="/catchupTV/:id">
                  <CatchupDetail />
                </Route>
                <Route exact path="/audio/:id">
                  <AudioDetail />
                </Route>
                <Route exact path="/content/audio/:slug">
                  <AudioDetailSlug />
                </Route>
                <Route exact path="/album/:id">
                  <AlbumDetail />
                </Route>
                <Route exact path="/content/album/:slug">
                  <AlbumDetailSlug />
                </Route>
                <Route exact path="/category/:id">
                  <CategoryPage />
                </Route>
                <Route exact path="/content/category/:slug">
                  <CategoryPageSlug />
                </Route>
                <Route exact path="/recommendation/:type">
                  <RecommendationPage />
                </Route>
                <Route exact path="/favourite">
                  <Favourite />
                </Route>
                <Route exact path="/favourites/:type">
                  <FavouriteSeeAll />
                </Route>
                <Route exact path="/privacy-policy">
                  <PrivacyPolicy />
                </Route>
                <Route exact path="/terms-condition">
                  <TermsOfUse />
                </Route>
                <Route exact path="/about-company">
                  <AboutCompany />
                </Route>
                <Route exact path="/contact-us">
                  <ContactUs />
                </Route>
                <Route exact path="/refund-policy">
                  <Pricing />
                </Route>
                <Route exact path="/allartists">
                  <AllPopularArtist />
                </Route>
                <Route exact path="/reels">
                  <Shorts />
                </Route>
                <Route exact path="/reels/:slug">
                  <ShortsBySlug />
                </Route>
                <PrivateRoute exact path="/settings/:name">
                  <Settings />
                </PrivateRoute>
                <PrivateRoute exact path="/search/:type">
                  <SearchPage />
                </PrivateRoute>
                <Route exact path="/subs">
                  <SubscribeRedirect />
                </Route>
                <PrivateRoute exact path="/connect-tv">
                  <ConnectTv />
                </PrivateRoute>
                <PrivateRoute exact path="/subscribe">
                  <PremiumModal2 />
                </PrivateRoute>
                <PrivateRoute exact path="/subscribe/:id">
                  <Package1 />
                </PrivateRoute>
                <Route exact path="/fb-promotion">
                  <FbPromotion />
                </Route>
                <Route exact path="/">
                  <Home />
                </Route>
                <Route path="*">
                  <NotFound />
                </Route>
              </Switch>
              <Modals />
            </ErrorBoundary>
          </ThemeProvider>
        </Suspense>
      </Layout>
    </IndicatorProvider>
  );
}
